import { deepCopy } from '../../../utils/CommonUtils';

export interface HistoryState<T> {
	data: T;
}

export interface UndoRedoHistory<T> {
	history: HistoryState<T>[];
	historyIndex: number;
	canUndo: boolean;
	canRedo: boolean;
}

export interface UndoRedoHistoryManager<T> {
	saveToHistory: (
		currentState: T,
		currentHistory: UndoRedoHistory<T>
	) => UndoRedoHistory<T>;
	undo: (
		currentHistory: UndoRedoHistory<T>
	) => { newHistory: UndoRedoHistory<T>; state: T | null };
	redo: (
		currentHistory: UndoRedoHistory<T>
	) => { newHistory: UndoRedoHistory<T>; state: T | null };
	clearHistory: () => UndoRedoHistory<T>;
	getInitialState: () => UndoRedoHistory<T>;
}

const MAX_HISTORY_SIZE = 5;

export function createUndoRedoHistoryManager<T>(
	maxHistorySize: number = MAX_HISTORY_SIZE
): UndoRedoHistoryManager<T> {
	const getInitialState = (): UndoRedoHistory<T> => ({
		history: [],
		historyIndex: -1,
		canUndo: false,
		canRedo: false,
	});

	const saveToHistory = (
		currentState: T,
		currentHistory: UndoRedoHistory<T>
	): UndoRedoHistory<T> => {
		const { history, historyIndex } = currentHistory;

		const newState: HistoryState<T> = {
			data: deepCopy(currentState),
		};

		const newHistory = history.slice(0, historyIndex + 1);
		newHistory.push(newState);

		if (newHistory.length > maxHistorySize) {
			newHistory.shift();
			return {
				history: newHistory,
				historyIndex: newHistory.length - 1,
				canUndo: true,
				canRedo: false,
			};
		}

		return {
			history: newHistory,
			historyIndex: newHistory.length - 1,
			canUndo: newHistory.length > 1,
			canRedo: false,
		};
	};

	const undo = (
		currentHistory: UndoRedoHistory<T>
	): { newHistory: UndoRedoHistory<T>; state: T | null } => {
		const { history, historyIndex } = currentHistory;

		if (historyIndex <= 0) {
			return { newHistory: currentHistory, state: null };
		}

		const newIndex = historyIndex - 1;
		const previousState = history[newIndex];

		return {
			newHistory: {
				history,
				historyIndex: newIndex,
				canUndo: newIndex > 0,
				canRedo: true,
			},
			state: deepCopy(previousState.data),
		};
	};

	const redo = (
		currentHistory: UndoRedoHistory<T>
	): { newHistory: UndoRedoHistory<T>; state: T | null } => {
		const { history, historyIndex } = currentHistory;

		if (historyIndex >= history.length - 1) {
			return { newHistory: currentHistory, state: null };
		}

		const newIndex = historyIndex + 1;
		const nextState = history[newIndex];

		return {
			newHistory: {
				history,
				historyIndex: newIndex,
				canUndo: true,
				canRedo: newIndex < history.length - 1,
			},
			state: deepCopy(nextState.data),
		};
	};

	const clearHistory = (): UndoRedoHistory<T> => {
		return getInitialState();
	};

	return {
		saveToHistory: (currentState: T, currentHistory: UndoRedoHistory<T>) =>
			saveToHistory(currentState, currentHistory),
		undo,
		redo,
		clearHistory,
		getInitialState,
	};
}

export interface CanvasState {
	nodes: any[];
	edges: any[];
}

export const canvasHistoryManager =
	createUndoRedoHistoryManager<CanvasState>();
